import * as React from "react"

import Modal from "@presentation/shared/components/modal/modal"

import type { ActionContext } from "./action-executor"
import { getErrorEntry } from "./error-catalog"
import { useErrorModalCtx } from "./error-modal-context"
import { DualCtaLayout } from "./variants/DualCtaLayout"
import { InfoLayout } from "./variants/InfoLayout"
import { PrimaryCtaLayout } from "./variants/PrimaryCtaLayout"
import { RedirectLayout } from "./variants/RedirectLayout"

/** Botón de cerrar con el ícono X circular del diseño. */
const CloseIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M11.9999 12.9798L15.2417 16.2219C15.3879 16.3678 15.5495 16.4382 15.7268 16.4329C15.9038 16.4275 16.0688 16.3483 16.2218 16.1955C16.3746 16.0426 16.451 15.8792 16.451 15.7055C16.451 15.5318 16.3746 15.3689 16.2218 15.2168L12.9797 11.9868L16.2218 8.75686C16.3677 8.61163 16.4425 8.4504 16.446 8.27317C16.4493 8.09611 16.3746 7.9311 16.2218 7.77813C16.0688 7.62534 15.9055 7.54895 15.7318 7.54895C15.5581 7.54895 15.3947 7.62534 15.2417 7.77813L11.9999 11.0202L8.75806 7.77813C8.61195 7.6322 8.45467 7.55748 8.28623 7.55396C8.11797 7.55062 7.95735 7.62534 7.80439 7.77813C7.6516 7.9311 7.5752 8.09444 7.5752 8.26816C7.5752 8.44187 7.65089 8.60521 7.80228 8.75818L11.0201 12L7.77801 15.2419C7.63208 15.388 7.56175 15.5453 7.56702 15.7137C7.57247 15.882 7.6516 16.0426 7.80439 16.1955C7.95735 16.3483 8.12069 16.4247 8.29441 16.4247C8.46812 16.4247 8.63067 16.349 8.78206 16.1977L11.9999 12.9798ZM12.0089 21.6C10.6883 21.6 9.44447 21.3502 8.27753 20.8505C7.11058 20.3508 6.08922 19.6632 5.21344 18.7878C4.33766 17.9124 3.64975 16.8918 3.1497 15.7261C2.64984 14.5606 2.3999 13.3151 2.3999 11.9897C2.3999 10.6644 2.64975 9.42262 3.14944 8.26446C3.64913 7.10631 4.33669 6.08934 5.21212 5.21356C6.08755 4.33778 7.10812 3.64987 8.27384 3.14983C9.43938 2.64996 10.6848 2.40002 12.0102 2.40002C13.3356 2.40002 14.5773 2.64987 15.7355 3.14956C16.8936 3.64925 17.9106 4.33682 18.7864 5.21224C19.6621 6.08767 20.3501 7.10613 20.8501 8.26763C21.35 9.4293 21.5999 10.6704 21.5999 11.9911C21.5999 13.3117 21.3501 14.5555 20.8504 15.7224C20.3507 16.8893 19.6631 17.9107 18.7877 18.7865C17.9123 19.6623 16.8938 20.3502 15.7323 20.8502C14.5706 21.3501 13.3295 21.6 12.0089 21.6ZM11.9999 20.2286C14.2856 20.2286 16.2285 19.4286 17.8285 17.8286C19.4285 16.2286 20.2285 14.2857 20.2285 12C20.2285 9.71431 19.4285 7.77145 17.8285 6.17145C16.2285 4.57145 14.2856 3.77145 11.9999 3.77145C9.71419 3.77145 7.77133 4.57145 6.17133 6.17145C4.57133 7.77145 3.77133 9.71431 3.77133 12C3.77133 14.2857 4.57133 16.2286 6.17133 17.8286C7.77133 19.4286 9.71419 20.2286 11.9999 20.2286Z"
      fill="#2F373C"
    />
  </svg>
)

/** Renderiza la variante correcta según el entry del catálogo. */
const ErrorModal: React.FC = () => {
  const { open, code, runtimeAction, closeModal } = useErrorModalCtx()

  if (!open) return null

  const entry = getErrorEntry(code)
  const ctx: ActionContext = { closeModal, runtimeAction }

  const renderVariant = () => {
    switch (entry.variant) {
      case "primary-cta":
        return <PrimaryCtaLayout entry={entry} ctx={ctx} />
      case "redirect":
        return <RedirectLayout entry={entry} ctx={ctx} />
      case "dual-cta":
        return <DualCtaLayout entry={entry} ctx={ctx} />
      case "info":
        return <InfoLayout entry={entry} ctx={ctx} />
    }
  }

  return (
    <Modal
      isOpen
      onClose={closeModal}
      hasClose={false}
      borderRadius={20}
      classBody="relative w-[328px] sm:w-[600px]"
    >
      <button
        type="button"
        onClick={closeModal}
        aria-label="Cerrar"
        className="absolute top-3 right-3 z-10"
      >
        <CloseIcon />
      </button>
      {renderVariant()}
    </Modal>
  )
}

export { ErrorModal }
